// LoginModal.jsx (React-Bootstrap Version)
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import { SET_LOADING, SET_USER } from "../redux/user";
import { toastErrorMessage, toastSuccessMessage } from "../utils/utils";
import { clearError, validateData } from "../validators/YupValidations";
import { loginSchema } from "../validators/validatorSchema";
import ForgotPassword from "./auth/ForgotPassword";
import SelectField from "./common/SelectField";
import { formatUSPhone, normalizePhoneForSave } from "../utils/phoneFormat";

const LoginModal = ({ isOpen, onClose }) => {
  const [loginType, setLoginType] = useState("email"); // "email" or "phone"
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
    country_code: "+1",
  });
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  // Autofocus when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (loginType === "email") {
          emailRef.current?.focus();
        } else {
          phoneRef.current?.focus();
        }
      }, 150);
    }
  }, [isOpen, loginType]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const dataToValidate = {
      ...formData,
      loginType,
    };

    if (loginType === "phone") {
      dataToValidate.phone =
        formData.country_code === "+1"
          ? normalizePhoneForSave(formData.phone)
          : formData.phone.replace(/\D/g, "");
    }

    const isValid = await validateData(loginSchema, dataToValidate, setErrors);
    if (!isValid) return;

    try {
      dispatch(SET_LOADING(true));

      const payload = {
        password: formData.password,
      };

      if (loginType === "email") {
        payload.email = formData.email;
      } else {
        payload.phone = dataToValidate.phone;
        payload.country_code = formData.country_code;
      }

      const response = await API.post(api_paths.auth.LOGIN, payload);

      if (response.data?.payload?.result) {
        const result = response.data.payload.result;
        dispatch(SET_USER(result.memberDoc));
        localStorage.setItem("GSTB_token", result.token);
        API.defaults.headers.common["Authorization"] = `Bearer ${result.token}`;

        toastSuccessMessage(response?.data?.message);

        onClose(); // Close modal
        navigate("/dashboard"); // Redirect
      } else {
        toastErrorMessage(response?.data?.message || "Login failed");
      }
    } catch (error) {
      toastErrorMessage(
        error?.response?.data?.message || "Something went wrong"
      );
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handlePhoneChange = (value) => {
    if (formData.country_code === "+1") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      value = formatUSPhone(digits);
    } else {
      value = value.replace(/\D/g, "").slice(0, 15);
    }
    setFormData((prev) => ({ ...prev, phone: value }));
    errors.phone && clearError(setErrors, "phone");
  };

  const goToRegister = () => {
    onClose();
    setTimeout(() => navigate("/register"), 200);
  };

  return (
    <>
      <Modal
        show={isOpen}
        onHide={onClose}
        centered
        backdrop="static"
        className={isForgotOpen ? "hide-modal-dialog" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-semibold">Login</Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4">
          <Form onSubmit={handleSubmit} noValidate>
            {/* Email / Phone Field */}
            {loginType === "email" ? (
              <Form.Group className="mb-3">
                <Form.Label className="small">Email</Form.Label>
                <Form.Control
                  ref={emailRef}
                  type="email"
                  placeholder="Enter your email"
                  className={`p-3 ${errors.email ? "is-invalid" : ""}`}
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    errors.email && clearError(setErrors, "email");
                  }}
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email.message}</div>
                )}
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label className="small">Phone Number</Form.Label>
                <div className="d-flex gap-2">
                  <div style={{ width: "100px" }}>
                    <SelectField
                      value={countryCodeOptions.find(
                        (opt) => opt.value === formData.country_code
                      )}
                      options={countryCodeOptions}
                      onChange={(opt) => {
                        const newCode = opt.value;
                        setFormData((prev) => ({
                          ...prev,
                          country_code: newCode,
                          phone:
                            newCode === "+1"
                              ? formatUSPhone(normalizePhoneForSave(prev.phone))
                              : prev.phone.replace(/\D/g, ""),
                        }));
                        errors.country_code && clearError(setErrors, "country_code");
                      }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <Form.Control
                      ref={phoneRef}
                      type="tel"
                      placeholder="Enter mobile number"
                      className={`p-3 ${errors.phone ? "is-invalid" : ""}`}
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      maxLength={formData.country_code === "+1" ? 14 : 15}
                    />
                    {errors.phone && (
                      <div className="invalid-feedback d-block">
                        {errors.phone.message}
                      </div>
                    )}
                  </div>
                </div>
              </Form.Group>
            )}

            {/* Password */}
            <Form.Group className="mb-3">
              <Form.Label className="small">Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  className={`p-3 ${errors.password ? "is-invalid" : ""}`}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    errors.password && clearError(setErrors, "password");
                  }}
                />
                <Button
                  variant="outline-secondary"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                >
                  {showPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
                </Button>
                {errors.password && (
                  <div className="invalid-feedback d-block">
                    {errors.password.message}
                  </div>
                )}
              </InputGroup>
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <Button
                onClick={() => setIsForgotOpen(true)}
                variant="link"
                className="forgot-link text-decoration-none p-0"
              >
                Forgot Password ?
              </Button>
              {/* <Button
                onClick={() => setLoginType(loginType === "email" ? "phone" : "email")}
                variant="link"
                className="forgot-link text-decoration-none p-0"
              >
                {loginType === "email" ? "Login with Phone" : "Login with Email"}
              </Button> */}
            </div>

            <Button type="submit" className="btn-orange-lg w-100 fw-semibold">
              Sign in
            </Button>

            <div className="text-center mt-3 mb-2">
              <span className="fw-medium no-account fw-medium">
                Don't Have An Account?
              </span>
              <span
                className="signup fw-normal ms-2 cursor-pointer"
                onClick={goToRegister}
              >
                Sign Up
              </span>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <ForgotPassword
        isOpen={isForgotOpen}
        onClose={() => setIsForgotOpen(false)}
      />
    </>
  );
};

export default LoginModal;
